import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {TextSpellcheckOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-spellcheck-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {Text, TextSize, TextType} from '@Components/text';
import type {UserDictionary} from '@Libraries/spell-check-library';
import {getUserDict, removeFromUserDictionary, updateSpellCheckCache} from '@Libraries/spell-check-library';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {updateSpellCheckSettingsState} from '@Components/poster-editor/poster-editor-reducer';
import {ConfirmDeleteIcon} from '@Components/contextual-menu/components/confirm-delete-icon';
import {v4 as uuidv4} from 'uuid';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useAppDispatch} from '@/hooks';
import type {Textbox} from '@postermywall/fabricjs-2';
import {noop} from '@Utils/general.util';

interface SpellcheckContextualItemsProps {
  showListOfIgnoredWordsOnly?: boolean;

  onWordsLoaded?: (numberOfWords: number) => void;
}

export function SpellcheckContextualItems({showListOfIgnoredWordsOnly = false, onWordsLoaded = noop}: SpellcheckContextualItemsProps): ReactElement<HTMLElement> {
  const [words, setWords] = useState<UserDictionary>();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshState, setRefreshState] = useState(false);
  const dispatch = useAppDispatch();

  const getUserDictionary = async (): Promise<void> => {
    try {
      const response = await getUserDict();
      setWords(response as UserDictionary);
      onWordsLoaded((response as UserDictionary).total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void getUserDictionary();
  }, [refreshState]);

  const onBackButton = (): void => {
    dispatch(updateSpellCheckSettingsState(false));
  };

  const getHeader = (): ReactElement<HTMLElement> => {
    return (
      <ContextualMenuHeader id="spellcheck-contextual-header">
        <ContextualMenuTextHeader text={window.i18next.t('pmwjs_spellcheck')} showBackButton onBackButton={onBackButton} />
      </ContextualMenuHeader>
    );
  };

  const removeWordFromIgnoredWordsList = (word: string): void => {
    const selectedItem = window.posterEditor?.whiteboard?.getSelectedItems()[0];
    void removeFromUserDictionary(word).then((): void => {
      updateSpellCheckCache(word, false);
      setRefreshState(!refreshState);
      const activeObjects = window.posterEditor?.whiteboard?.getSelectedViews();
      if (activeObjects) {
        const fabricTextbox = activeObjects[0];
        void selectedItem?.page?.spellCheck?.checkSpell(fabricTextbox as Textbox);
      }
    });
  };

  const getWords = (): ReactElement<HTMLElement> | ReactElement<HTMLElement>[] => {
    if (isLoading) {
      return (
        <div className="flex-v-row flex-items-center">
          <CircularProgressLoader classes="spacing-m-0" size={LOADER_SIZE.DEFAULT} />
        </div>
      );
    }

    if (words?.total === 0 || words?.dict.length === 0 || !words) {
      return (
        <div className="_full-width flex-column-justify-center flex-items-center">
          <Icon shape={IconShape.CIRCLE} type={IconType.FLAT} size={IconSize.SIZE_ICON_24} icon="icon-ban" />
          <Text className="spacing-p-t-3" type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_no_ignored_words_in_dict')} />
        </div>
      );
    }

    return words.dict.map((word) => {
      const id = uuidv4();
      return (
        <li key={id} className="flex-row-justify-between flex-items-center spacing-p-0 spacing-m-b-3">
          <Text size={TextSize.XSMALL} val={word} />
          <ConfirmDeleteIcon
            id={id}
            onDelete={(): void => {
              removeWordFromIgnoredWordsList(word);
            }}
          />
        </li>
      );
    });
  };

  const getSpellcheckContextualItemContent = (): ReactElement<HTMLElement> => {
    if (showListOfIgnoredWordsOnly) {
      return <ul className="spacing-m-0">{getWords()}</ul>;
    }
    return (
      <ContextualMenu id="spellcheck-contextual-options" header={getHeader()}>
        <TextSpellcheckOption />
        <ContextualMenuHeadingOption id="spellcheck-ignored_words" text={window.i18next.t('pmwjs_ignored_words')} />
        <ul className="spacing-m-0">{getWords()}</ul>
      </ContextualMenu>
    );
  };

  return getSpellcheckContextualItemContent();
}
